import { readErrorMessage } from "../../utils/apiError";
import React, { useEffect, useState } from "react";
import { API_BASE_URL } from "../../utils/constants";
import LoadingSpinner from "../../components/LoadingSpinner";
import ClearForm from "../../components/Clearform";
import { Form } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import { useAuth } from "../../components/AuthProvider";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../../styles/datepicker-custom.css";
import { useAvailableDates } from "../../hooks/useAvailableDates";
import LotFilter, { filterByLot } from "../../components/LotFilter";

const SecondHearingRoznama = () => {
    const { user } = useAuth();
    const [secondHearingDate, setSecondHearingDate] = useState("");
    const [roznamaCases, setRoznamaCases] = useState([]);
    const [selectedCases, setSelectedCases] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showTable, setShowTable] = useState(false);
    const [pdfUrl, setPdfUrl] = useState("");
    const [showPDF, setShowPDF] = useState(false);
    const [clearForm, setClearForm] = useState(false);
    const [arbId, setArbId] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [lotFilter, setLotFilter] = useState("");
    const rowsPerPage = 10;

    useEffect(() => {
        if (user && user[0]) {
            setArbId(user[0].Ref_id);
        }
    }, [user]);

    // Use the custom hook to fetch available dates
    const { availableDates, availableDateObjects, openToDate } = useAvailableDates('AvailableSecondHearingDates', arbId);

    const handleDateChange = (date) => {
        if (date) {
            // Format date as YYYY-MM-DD in local timezone
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const formattedDate = `${year}-${month}-${day}`;
            setSecondHearingDate(formattedDate);
            // Auto-fetch cases when date is selected
            setTimeout(() => handleFetchCases(formattedDate), 100);
        } else {
            setSecondHearingDate("");
        }
    };

    const handleFetchCases = async (selectedDate = null) => {
        const dateToFetch = selectedDate || secondHearingDate;
        if (!dateToFetch) {
            toast.error("Please select a second hearing date");
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(
                `${API_BASE_URL}/api/SecondHearingRoznamaCases?Arb_id=${arbId}&Second_hearing_date=${dateToFetch}`
            );

            if (!response.ok) {
                throw new Error(await readErrorMessage(response));
            }

            const result = await response.json();
            const cases = result.data || [];
            setRoznamaCases(cases);
            setShowTable(cases.length > 0);

            if (cases.length === 0) {
                toast.info("No cases found for the selected date");
            }
        } catch (error) {
            console.error("Error fetching cases:", error);
            toast.error("Failed to fetch cases");
        } finally {
            setLoading(false);
        }
    };

    const handleCheckboxChange = (caseId) => {
        setSelectedCases((prev) => {
            if (prev.includes(caseId)) {
                return prev.filter((id) => id !== caseId);
            } else {
                return [...prev, caseId];
            }
        });
    };

    const handleSelectAll = () => {
        // Only the cases shown under the current lot / search filters
        if (filteredCases.length > 0 && filteredCases.every((c) => selectedCases.includes(c.Case_id))) {
            setSelectedCases([]);
        } else {
            setSelectedCases(filteredCases.map((c) => c.Case_id));
        }
    };

    const handlePreviewRoznama = async (caseId) => {
        setLoading(true);
        try {
            const response = await fetch(
                `${API_BASE_URL}/api/GenerateSecondHearingRoznama?Case_id=${caseId}`
            );

            if (!response.ok) {
                throw new Error(await readErrorMessage(response));
            }

            const pdfBlob = await response.blob();
            const pdfUrl1 = URL.createObjectURL(pdfBlob);
            setPdfUrl(pdfUrl1);
            setShowPDF(true);
        } catch (error) {
            console.error("Error generating roznama:", error);
            toast.error(error.message || "Failed to generate roznama");
        } finally {
            setLoading(false);
        }
    };

    const handleSaveRoznamas = async () => {
        if (selectedCases.length === 0) {
            toast.error("Please select at least one case");
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(
                `${API_BASE_URL}/api/SaveSecondHearingRoznama`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        Case_ids: selectedCases,
                    }),
                }
            );

            if (!response.ok) {
                throw new Error(await readErrorMessage(response));
            }

            const result = await response.json();

            setShowTable(false);
            setClearForm(true);
            toast.success(result.message);

            setTimeout(() => {
                setClearForm(false);
                setShowPDF(false);
                setRoznamaCases([]);
                setSelectedCases([]);
                setSecondHearingDate("");
                setSearchTerm("");
                setCurrentPage(1);
            }, 2000);
        } catch (error) {
            console.error("Error saving roznamas:", error);
            toast.error("Failed to save roznamas");
        } finally {
            setLoading(false);
        }
    };

    const searchFields = ["Reference_No", "Cust_name", "Lot_no", "bankName"];
    const filteredCases = filterByLot(roznamaCases, lotFilter).filter((item) =>
        searchFields.some((key) =>
            item[key]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
    );

    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentRows = filteredCases.slice(indexOfFirstRow, indexOfLastRow);
    const totalPages = Math.ceil(filteredCases.length / rowsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    if (loading) return <LoadingSpinner />;

    return (
        <div className="container-fluid px-3">
            <div className="row align-items-center mb-3">
                <div className="col-12 col-md-8 mb-2 mb-md-0">
                    <h5 className="mb-0">Second Hearing Roznama</h5>
                </div>
                {showTable && !showPDF && !clearForm && selectedCases.length > 0 && (
                    <div className="col-12 col-md-4 text-md-end">
                        <button
                            className="custBtn"
                            onClick={handleSaveRoznamas}
                            disabled={loading}
                        >
                            {loading ? "Saving..." : `Save Selected (${selectedCases.length})`}
                        </button>
                    </div>
                )}
            </div>

            {!showTable && !showPDF && !clearForm && (
                <div className="row align-items-center">
                    <div className="col-12 col-md-2 mb-3">
                        <Form.Group>
                            <Form.Label>Select Second Hearing Date</Form.Label>
                        </Form.Group>
                    </div>
                    <div className="col-12 col-md-2 mb-3">
                        <Form.Group>
                            <DatePicker
                                selected={secondHearingDate ? (() => {
                                    const [year, month, day] = secondHearingDate.split('-').map(Number);
                                    return new Date(year, month - 1, day);
                                })() : null}
                                onChange={handleDateChange}
                                includeDates={availableDateObjects}
                                openToDate={openToDate}
                                dateFormat="yyyy-MM-dd"
                                placeholderText="Select a date"
                                className="form-control custom_input"
                                disabled={availableDates.length === 0}
                            />
                        </Form.Group>
                    </div>
                    {availableDates.length === 0 && (
                        <div className="col-12 col-md-4 mb-3">
                            <small className="text-muted">No second hearing roznama dates found</small>
                        </div>
                    )}
                </div>
            )}

            {showTable && !showPDF && !clearForm && (
                <div className="row">
                    <div className="col-12">
                        <div className="mb-3 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
                            <div className="d-flex flex-wrap align-items-center gap-2">
                                <input
                                    type="text"
                                    placeholder="Search..."
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    className="form-control"
                                    style={{ maxWidth: "300px" }}
                                />
                                <LotFilter
                                    rows={roznamaCases}
                                    value={lotFilter}
                                    onChange={(next) => { setLotFilter(next); setSelectedCases([]); setCurrentPage(1); }}
                                />
                            </div>
                            <button className="custBtn" onClick={handleSelectAll}>
                                {filteredCases.length > 0 && filteredCases.every((c) => selectedCases.includes(c.Case_id))
                                    ? "Deselect All"
                                    : "Select All"}
                            </button>
                        </div>

                        <div className="table-responsive">
                            <table className="responsive-table my-3">
                                <thead className="text-center">
                                    <tr className="table-info">
                                        <th scope="col" style={{ width: "50px" }}>
                                            <input
                                                type="checkbox"
                                                checked={selectedCases.length === roznamaCases.length && roznamaCases.length > 0}
                                                onChange={handleSelectAll}
                                            />
                                        </th>
                                        <th scope="col" style={{ width: "60px" }}>Sr No</th>
                                        <th scope="col">Reference No</th>
                                        <th scope="col">Customer Name</th>
                                        <th scope="col" style={{ width: "80px" }}>Lot No</th>
                                        <th scope="col">Bank</th>
                                        <th scope="col" style={{ width: "110px" }}>Second Hearing</th>
                                        <th scope="col" style={{ width: "110px" }}>Third Hearing</th>
                                        <th scope="col" style={{ width: "100px" }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentRows.length > 0 ? (
                                        currentRows.map((item, index) => (
                                            <tr key={item.Case_id}>
                                                <td className="text-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedCases.includes(item.Case_id)}
                                                        onChange={() => handleCheckboxChange(item.Case_id)}
                                                    />
                                                </td>
                                                <td className="text-center">{indexOfFirstRow + index + 1}</td>
                                                <td className="text-center">{item.Reference_No}</td>
                                                <td className="text-center">{item.Cust_name}</td>
                                                <td className="text-center">{item.Lot_no}</td>
                                                <td>{item.bankName}</td>
                                                <td className="text-center">{item.Second_Hearing_date}</td>
                                                <td className="text-center">{item.Third_hearing_date || "N/A"}</td>
                                                <td className="text-center">
                                                    <button
                                                        onClick={() => handlePreviewRoznama(item.Case_id)}
                                                        className="custBtn"
                                                    >
                                                        Preview
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="9" className="text-center text-muted py-3">
                                                No results found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {totalPages > 1 && (
                            <div className="d-flex justify-content-center mt-3">
                                <nav>
                                    <ul className="pagination">
                                        {Array.from({ length: totalPages }, (_, i) => (
                                            <li
                                                key={i + 1}
                                                className={`page-item ${currentPage === i + 1 ? "active" : ""}`}
                                            >
                                                <button
                                                    className="page-link"
                                                    onClick={() => handlePageChange(i + 1)}
                                                >
                                                    {i + 1}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </nav>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {showPDF && !clearForm && (
                <div className="row">
                    <div className="col-12 mb-3">
                        <button className="custBtn" onClick={() => setShowPDF(false)}>
                            ← Back to List
                        </button>
                    </div>
                    <div className="col-12">
                        <div style={{ height: "calc(100vh - 200px)", width: "100%" }}>
                            <iframe
                                src={pdfUrl}
                                style={{ width: "100%", height: "100%", border: "1px solid #ddd", borderRadius: "4px" }}
                                title="Roznama PDF Viewer"
                            />
                        </div>
                    </div>
                </div>
            )}

            {clearForm && (
                <div className="row">
                    <div className="col-12 d-flex justify-content-center">
                        <ClearForm
                            message="Second Hearing Roznama Saved Successfully!"
                            redirectPath="/arbdashboard/secondHearing/secondHearingRoznama"
                        />
                    </div>
                </div>
            )}

            <ToastContainer />
        </div>
    );
};

export default SecondHearingRoznama;
